import { useAuth0 } from "@auth0/auth0-react";
import { animated, useSpring } from '@react-spring/web';
import AuthService from "Lib/API/Auth";
import { MyCheckbox } from "components";
import Loader from "components/loader/Loader";
import { FormikProvider, useFormik } from "formik";
import TextEditor from "components/FormManage/text-editor";
import { SubHeader } from "layout/SubHeader";
import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { formatPhoneNumber } from 'react-phone-number-input';
import { useDispatch } from "react-redux";
import { toastr } from "react-redux-toastr";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { sanitizeRows } from "utils/exportSanitize";
import { HeaderLogo, HeaderLogoInBase } from "../../store/redux/actions";
import { uploadFile } from "../Auth/Presigned";
import PersonalInfoEdit from "./editInfo";
import CheckValidationSchema from "./validationSchema";

import { ReactComponent as ExportYahIcon } from "assets/icons/export_yah.svg";
import ControlPointIcon from "@mui/icons-material/ControlPoint";
import Button from "components/button/Button";
import InfoTooltip from "components/InfoTooltip";
import { setLocalizationMoment } from "helper/dateFormat";
import "./index.scss";

const SettingsPage = () => {
  //   const settingsUserData = useSelector((data) => data?.dashboardData);
  const [updateData, setUpdateData] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [userInfo, setUserInfo] = useState();
  const { getAccessTokenSilently, user } = useAuth0();
  const orgDetails = JSON.parse(localStorage.getItem("organization"));

  const appDomain = process.env.REACT_APP_SUBDOMAIN || "easyshul.com";
  const organizationUrl = `https://${orgDetails?.orgkey}.${appDomain}`;

  const memberSite = `${organizationUrl}`;
  const donationPage = `${organizationUrl}/donation`;
  const educationPage = `${organizationUrl}/school`;
  const paymentPage = `${organizationUrl}/charge`;
  const eventsCalendarPage = `${organizationUrl}/events-calendar`;
  const [getInitialValues, setInitialValues] = useState("");
  const [pictures, setPictures] = useState(null);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [downloadDirectoryLoading, setDownloadDirectoryLoading] = useState(false);
  const [calendarId, setCalendarId] = useState('');
  const [calendarApiKey, setCalendarApiKey] = useState('');
  const [showCalendarInputs, setShowCalendarInputs] = useState(false);

  //   let abc = localStorage.getItem("refresh");
  useEffect(() => {
    async function fetchMyAPI() {
      const token = await getAccessTokenSilently();
      setLoading(true);
      AuthService.getUserExist(token)
        .then((res) => {
          localStorage.setItem(
            "organization",
            JSON.stringify(res?.user?.permissions?.organization)
          );
          localStorage.setItem(
            "authorizedModules",
            JSON.stringify(res?.user?.permissions?.authorizedModules)
          );
          setUserInfo(res);
          setUpdateData(false);
          setLocalizationMoment(res?.user?.permissions?.organization?.country?.local);
          const gcId = res?.user?.permissions?.organization?.googleCalendarId || '';
          const gcKey = res?.user?.permissions?.organization?.googleCalendarApiKey || '';
          setCalendarId(gcId);
          setCalendarApiKey(gcKey);
          if (gcId || gcKey) setShowCalendarInputs(true);
        })
        .catch((err) => {
          toastr.error("Error", err?.data?.message);
          setUserInfo(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
    fetchMyAPI();
  }, [getAccessTokenSilently, updateData, refresh]);


  const copyEventCalendarIframe = () => {
    const el = document.createElement('textarea');
    el.value = `<iframe src="${eventsCalendarPage}" width="100%" height="700px" frameborder="0" title="Events"></iframe>`;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    toastr.success("Success", "Copied iframe code to clipboard");
  }

  const exportDirectoryHandler = (list) => {
    let directoryData = [];
    const dataExport = list?.map((item) => {
      const data = {
        ["Contact Id"]: item?.member_id?.toString(),
        ["Family Name"]: item?.familyName,
        ["First Name"]: item?.firstName,
        ["Last Name"]: item?.lastName,
        ["Mobile"]: formatPhoneNumber(item?.mobileNumber),
        ["Home Phone"]: formatPhoneNumber(item?.homeNumber),
        ["Email"]: item?.emailAddress,
        ["Address"]: item?.address?.formattedAddress,
      };
      directoryData.push(data);
    });
    const worksheet = XLSX.utils.json_to_sheet(sanitizeRows(directoryData));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(
      workbook,
      "Members directory.xlsx"
    );
  }



  const downloadDirectory = async () => {
    if(downloadDirectoryLoading) return;
    setDownloadDirectoryLoading(true);
    const token = await getAccessTokenSilently();
    await AuthService.getDirectoryList(token, 0, 0)
      .then((res) => {
        const result = res?.payload?.data;
        exportDirectoryHandler(result);
      })
      .catch((err) => {
        toastr.error("Error", err?.data?.message || "Something went wrong!");
      })
      .finally(() => {
        setDownloadDirectoryLoading(false);
      });
  }

  const checkAtLeastOneMembersSettingsEnabled = (values) => {
    return values?.enableMembers || values?.enableYahrzeit || values?.enableEducation || values?.enableBilling || values?.enableEvents || values?.enableDonations || values?.enableSettings || values?.memberPortalDirectory
  }


  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      logoImg: userInfo?.user?.permissions?.organization?.logoImg,
      organizationName: userInfo?.user?.permissions?.organization?.orgName,
      shulwebsitelink: userInfo?.user?.permissions?.organization?.shul_website,
      country: userInfo?.user?.permissions?.organization?.country,
      emailAddress:
        userInfo?.user?.permissions?.organization?.contactInfo?.email,
      address: userInfo?.user?.permissions?.organization?.contactInfo?.address,

      name:
        userInfo?.user?.permissions?.organization?.billing_contact?.name ?? "",
      billingEmailAddress:
        userInfo?.user?.permissions?.organization?.billing_contact?.email ?? "",

      phoneNumber: [
        {
          contact_name:
            userInfo?.user?.permissions?.organization?.contactInfo
              ?.contactPerson ?? "",
          phone_number:
            userInfo?.user?.permissions?.organization?.contactInfo?.phone ?? "",
          preferredNumber: false,
          emergencyNumber: false,
        },
      ],
      disableEditingOnMemberPortal: userInfo?.user?.permissions?.organization?.disableEditingOnMemberPortal ?? false,
      memberPortalDirectory: userInfo?.user?.permissions?.organization?.memberPortalDirectory ?? false,
      allowBillMyAccountOnDonation: userInfo?.user?.permissions?.organization?.allowBillMyAccountOnDonation ?? false,
      allowInstallments: userInfo?.user?.permissions?.organization?.allowInstallments ?? false,
      allowEditOnExternal: userInfo?.user?.permissions?.organization?.allowEditOnExternal ?? false,
      sendReceiptOnPayment: userInfo?.user?.permissions?.organization?.sendReceiptOnPayment ?? false,
      memberPortal: userInfo?.user?.permissions?.organization?.memberPortal ?? false,
      enableMembers: userInfo?.user?.permissions?.organization?.memberSetting?.enableMembers ?? false,
      enableYahrzeit: userInfo?.user?.permissions?.organization?.memberSetting?.enableYahrzeit ?? false,
      enableEducation: userInfo?.user?.permissions?.organization?.memberSetting?.enableEducation ?? false,
      enableBilling: userInfo?.user?.permissions?.organization?.memberSetting?.enableBilling ?? false,
      enableEvents: userInfo?.user?.permissions?.organization?.memberSetting?.enableEvents ?? false,
      enableDonations: userInfo?.user?.permissions?.organization?.memberSetting?.enableDonations ?? false,
      enableSettings: userInfo?.user?.permissions?.organization?.memberSetting?.enableSettings ?? false,
      announcement: userInfo?.user?.permissions?.organization?.memberSetting?.announcement ?? "",
      memberPortalApp: userInfo?.user?.permissions?.organization?.memberPortalApp ?? false,
      memberAppAnnouncement: userInfo?.user?.permissions?.organization?.memberApp?.announcement ?? "",

    },
    validationSchema: CheckValidationSchema,

    onSubmit: async (values) => {
      setInitialValues(values);
      const token = await getAccessTokenSilently();
      const isValidMembersSettings = values?.memberPortal ? checkAtLeastOneMembersSettingsEnabled(values) : true;
      if(!isValidMembersSettings){
        return toastr.info("Validation", "At least one member module should be enabled");
      }

      const newUrl = pictures
        ? await uploadFile(pictures, pictures, token)
        : "";

      const contactData = values?.phoneNumber[0];

      let input =
        values?.provider?.value === "Stripe"
          ? {
              org_Id: orgDetails?._id,
              orgkey: orgDetails?.orgkey,
              orgName: values?.organizationName,
              shul_website: values?.shulwebsitelink,
              country: values?.country,
              contactInfo: {
                phone: contactData?.phone_number,
                email: values?.emailAddress,
                contactPerson: contactData?.contact_name,
                address: values?.address,
              },
              billing_contact: {
                name: values?.name,
                email: values?.billingEmailAddress,
              },
              disableEditingOnMemberPortal: values?.disableEditingOnMemberPortal,
              memberPortalDirectory: values?.memberPortalDirectory,
              allowBillMyAccountOnDonation: values?.allowBillMyAccountOnDonation,
              allowInstallments: values?.allowInstallments,
              allowEditOnExternal: values?.allowEditOnExternal,
              sendReceiptOnPayment: values?.sendReceiptOnPayment,
              memberPortal: values?.memberPortal,
              memberSetting: {
                enableMembers: values?.enableMembers,
                enableYahrzeit: values?.enableYahrzeit,
                enableEducation: values?.enableEducation,
                enableBilling: values?.enableBilling,
                enableEvents: values?.enableEvents,
                enableDonations: values?.enableDonations,
                enableSettings: values?.enableSettings,
                announcement: values?.announcement,
              },
              memberAppAnnouncement: new DOMParser().parseFromString(values?.memberAppAnnouncement ?? "", "text/html").body.textContent?.trim() ?? "",
              googleCalendarId: calendarId,
              googleCalendarApiKey: calendarApiKey,
            }
          : {
              org_Id: orgDetails?._id,
              orgkey: orgDetails?.orgkey,
              orgName: values?.organizationName,
              shul_website: values?.shulwebsitelink,
              country: values?.country,
              contactInfo: {
                phone: contactData?.phone_number,
                email: values?.emailAddress,
                contactPerson: contactData?.contact_name,
                address: values?.address,
              },
              billing_contact: {
                name: values?.name,
                email: values?.billingEmailAddress,
              },
              disableEditingOnMemberPortal: values?.disableEditingOnMemberPortal,
              memberPortalDirectory: values?.memberPortalDirectory,
              allowBillMyAccountOnDonation: values?.allowBillMyAccountOnDonation,
              allowInstallments: values?.allowInstallments,
              allowEditOnExternal: values?.allowEditOnExternal,
              sendReceiptOnPayment: values?.sendReceiptOnPayment,
              memberPortal: values?.memberPortal,
              memberSetting: {
                enableMembers: values?.enableMembers,
                enableYahrzeit: values?.enableYahrzeit,
                enableEducation: values?.enableEducation,
                enableBilling: values?.enableBilling,
                enableEvents: values?.enableEvents,
                enableDonations: values?.enableDonations,
                enableSettings: values?.enableSettings,
                announcement: values?.announcement,
              },
              googleCalendarId: calendarId,
              googleCalendarApiKey: calendarApiKey,
              memberAppAnnouncement: new DOMParser().parseFromString(values?.memberAppAnnouncement ?? "", "text/html").body.textContent?.trim() ?? "",
            };

      if (typeof newUrl.fileName === "string") {
        dispatch(HeaderLogo(newUrl.link));
        // localStorage.setItem("logoImg", JSON.stringify(newUrl.link));
        input.logoImg = newUrl.fileName;
      }

      await AuthService.updateOrgSettings(orgDetails?._id, input, token)
        .then((res) => {
          setUpdateData(true);
          setRefresh(!refresh);
          // window.location.href = "/settings";
          toastr.success("Success", "Updated successfully");
          window.dispatchEvent(new CustomEvent("logoImg"));
        })
        .catch((error) => {
          toastr.error(
            "Update Error",
            error?.data?.message ?? "Some fields are missing"
          );
        });
    },
  });



  const contentSpring = useSpring({
    height: formik.values?.memberPortal ? 750 : 0,
    opacity: formik.values?.memberPortal ? 1 : 0,
    config: { mass: 1, tension: 200, friction: 24 },
  })
    
  return (
    <FormikProvider value={formik}>
     {loading  ? (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
          <Loader />
        </div>
      ) : (
      <div className="m-0 w-100">
        <div className="m-0 w-100 ">
          <div className="settings-container row">
            <div className="profile-data col-md-5">
              <Card className="card card-custom p-4 mt-4 ">
                <div className="profile-links">
                  <div className="user-info">
                    <div>
                      <img
                        alt=""
                        src={user?.picture}
                        style={{ objectFit: "fill" }}
                        height="102"
                        width="99"
                        className="with-border"
                        name="profile_img"
                      />
                    </div>
                    <div className="user">
                      <div className="user-name">{user?.nickname}</div>
                      {/* <div className="hebrew-name">נסיף ג’וש קורדיצור</div> */}
                      <div className="user-email">{user?.email}</div>
                    </div>
                  </div>
                  {/* <div>
                    <div className="user-data pt-10">
                      <div className="info-head col-sm-5">Email:</div>
                      <div className="info-data">
                        <div>
                          {
                            userInfo?.user?.permissions?.organization
                              ?.contactInfo?.email
                          }
                        </div>
                      </div>
                    </div>
                    <div className="user-data">
                      <div className="info-head col-sm-5">Phone:</div>
                      <div className="info-data">
                        <div>
                          {
                            userInfo?.user?.permissions?.organization
                              ?.contactInfo?.phone
                          }
                        </div>
                      </div>
                    </div>
                    <div className="user-data">
                      <div className="info-head col-sm-5">Address:</div>
                      <div className="info-data">
                        <div>
                          {
                            userInfo?.user?.permissions?.organization
                              ?.contactInfo?.address
                          }
                        </div>
                      </div>
                    </div>
                  </div> */}
                </div>
              </Card>
              <Card className="card card-custom p-4 mt-4 ">
                <div className="profile-links">
                  <div className="user-info">
                    {/* <div>
                        <img
                          alt=""
                          src={user?.picture}
                          style={{ objectFit: "fill" }}
                          height="102"
                          width="99"
                          className="with-border"
                          name="profile_img"
                        />
                      </div> */}
                    <div className="user">
                      <div className="user-name">Organization links</div>
                      {/* <div className="hebrew-name">נסיף ג’וש קורדיצור</div> */}
                      {/* <div className="user-email">{user?.email}</div> */}
                    </div>
                  </div>
                  <div>
                    <div className="user-data pt-10">
                      <div className="info-head col-4" style={{
                        whiteSpace: 'nowrap',
                      }}>Member site:</div>
                      <div className="info-data">
                        <div>
                          <Link to={{ pathname: memberSite }} target="_blank">
                            {memberSite}
                          </Link>
                        </div>
                      </div>
                    </div>
                    <div className="user-data">
                      <div className="info-head col-4 whiteSpace-nowrap">Donation page</div>
                      <div className="info-data">
                        <Link to={{ pathname: donationPage }} target="_blank">
                          {donationPage}
                        </Link>
                      </div>
                    </div>
                    <div className="user-data">
                      <div className="info-head col-4 whiteSpace-nowrap">Education page</div>
                      <div className="info-data">
                        <Link to={{ pathname: educationPage }} target="_blank">
                          {educationPage}
                        </Link>
                      </div>
                    </div>
                    {/* <div className="user-data">
                      <div className="info-head col-4 whiteSpace-nowrap">Payment page</div>
                      <div className="info-data">
                        <Link to={{ pathname: paymentPage }} target="_blank">
                          {paymentPage}
                        </Link>
                      </div>
                    </div> */}
                    <div className="user-data">
                      <div className="info-head col-4 whiteSpace-nowrap">Events Calendar</div>
                      <div className="info-data d-flex flex-column align-items-start flex-grow-1">
                        <Link to={{ pathname: eventsCalendarPage }} target="_blank">
                          {eventsCalendarPage}
                        </Link>
                      </div>
                    </div>
                    <div className="user-data" style={{ alignItems: showCalendarInputs ? 'flex-start' : 'center' }}>
                      <div className="info-head col-4 whiteSpace-nowrap d-flex align-items-center" style={{ gap: '6px' }}>
                        Google Calendar
                        <InfoTooltip placement="bottom" text={<>Connect your Google Calendar to display events publicly.<br /><br />To get your credentials:<br />1) Open calendar.google.com<br />2) Click ⋮ next to your calendar → Settings and sharing<br />3) Scroll to 'Integrate calendar' and copy the Calendar ID<br />4) Go to console.cloud.google.com → APIs & Services → Credentials to get your API key</>} />
                      </div>
                      <div className="info-data flex-grow-1">
                        {!showCalendarInputs ? (
                          <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                            <ControlPointIcon
                              style={{ color: '#2555EF', cursor: 'pointer', fontSize: 20 }}
                              onClick={() => setShowCalendarInputs(true)}
                            />
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                              <input
                                type="text"
                                className="google-calendar-input"
                                placeholder="Enter Calendar ID"
                                value={calendarId}
                                onChange={e => setCalendarId(e.target.value)}
                              />
                            </div>
                            <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                              <input
                                type="text"
                                className="google-calendar-input"
                                placeholder="Enter API Key"
                                value={calendarApiKey}
                                onChange={e => setCalendarApiKey(e.target.value)}
                              />
                              <div style={{ visibility: 'hidden', flexShrink: 0 }}><InfoTooltip text="" /></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="user-data" style={{ justifyContent: 'flex-end' }}>
                      <Button
                        className="mt-2"
                        onClick={copyEventCalendarIframe}
                        size="sm"
                      >Copy iframe code</Button>
                    </div>
                  </div>
                </div>

              </Card>
              <Card className="card card-custom p-4 mt-4 ">
                    <div className="organization-settings">
                      <div className="organization-settings__head">
                        <div className="organization-settings-label">Payment Settings</div>
                      </div>
                      <div className="organization-settings__content">
                        <div className="organization-settings__content-field" style={{ display: 'flex', alignItems: 'center' }}>
                          <MyCheckbox
                            name={`allowInstallments`}
                            title="Allow installments"
                            handlechange={(event) => {
                              formik.setFieldValue("allowInstallments", event.target.checked);
                            }}
                            checked={formik.values?.allowInstallments}
                          />
                          <InfoTooltip text="Lets members split a charge into multiple scheduled payments instead of paying the full amount at once, org-wide." style={{ marginLeft: '6px', marginBottom: '8px' }} />
                        </div>
                        <div className="organization-settings__content-field" style={{ display: 'flex', alignItems: 'center' }}>
                          <MyCheckbox
                            name={`allowEditOnExternal`}
                            title="Allow edit amount on the external payment page"
                            handlechange={(event) => {
                              formik.setFieldValue("allowEditOnExternal", event.target.checked);
                            }}
                            checked={formik.values?.allowEditOnExternal}
                            style={{
                              alignItems: "flex-start"
                            }}
                            titleStyles={{
                              whiteSpace: "pre-wrap"
                            }}
                          />
                          <InfoTooltip text="Lets the payer change the payment amount on the public/external payment page, instead of paying the full amount." style={{ marginLeft: '6px', marginBottom: '8px' }} />
                        </div>
                        <div className="organization-settings__content-field" style={{ display: 'flex', alignItems: 'center' }}>
                          <MyCheckbox
                            name={`allowBillMyAccountOnDonation`}
                            title="Allow bill my account on donation page"
                            handlechange={(event) => {
                              formik.setFieldValue("allowBillMyAccountOnDonation", event.target.checked);
                            }}
                            checked={formik.values?.allowBillMyAccountOnDonation}
                            style={{
                              alignItems: "flex-start"
                            }}
                            titleStyles={{
                              whiteSpace: "pre-wrap"
                            }}
                          />
                          <InfoTooltip text="Lets donors choose to bill the charge to their account instead of paying immediately on the donation page." style={{ marginLeft: '6px', marginBottom: '8px' }} />
                        </div>
                        <div className="organization-settings__content-field" style={{ display: 'flex', alignItems: 'center' }}>
                          <MyCheckbox
                            name={`sendReceiptOnPayment`}
                            title="Send receipt on payment"
                            handlechange={(event) => {
                              formik.setFieldValue("sendReceiptOnPayment", event.target.checked);
                            }}
                            checked={formik.values?.sendReceiptOnPayment}
                            style={{
                              alignItems: "flex-start"
                            }}
                            titleStyles={{
                              whiteSpace: "pre-wrap"
                            }}
                          />
                          <InfoTooltip text="Automatically emails the payer a receipt whenever a payment is successfully made." style={{ marginLeft: '6px', marginBottom: '8px' }} />
                        </div>
                      </div>
                    </div>
                
              </Card>
              <Card className="card card-custom p-4 mt-4 "
                style={{
                  minHeight: "500px"
                }}
              >
                  <div className="organization-settings">
                    <div className="organization-settings__head">
                      <div className="organization-settings-label">Member Portal Settings</div>
                    </div>
                    <div className="organization-settings__content">
                        <div className="organization-settings__content-field" style={{ display: 'flex', alignItems: 'center' }}>
                          <MyCheckbox
                              name={`memberPortal`}
                              title="Enable members portal"
                              handlechange={(event) => {
                                formik.setFieldValue("memberPortal", event.target.checked);
                              }}
                              checked={formik.values?.memberPortal}
                            />
                          <InfoTooltip text="Master switch for the member portal. Turning this on reveals the settings below to control what members can see and do." style={{ marginLeft: '6px', marginBottom: '8px' }} />
                        </div>
                    </div>
                    <animated.div 
                        style={{
                          overflow: 'hidden',
                          ...contentSpring
                        }}
                      >
                        <div className="organization-settings__content-field" style={{ display: 'flex', alignItems: 'center' }}>
                          <MyCheckbox
                              name={`default_bank_account`}
                              title="Disable editing on member portal"
                              handlechange={(event) => {
                                formik.setFieldValue("disableEditingOnMemberPortal", event.target.checked);
                              }}
                              checked={formik.values?.disableEditingOnMemberPortal}
                            />
                          <InfoTooltip text="Prevents members from editing their own info (e.g. contact details) when logged into the member portal." placement="bottom" useFixed style={{ marginLeft: '6px', marginBottom: '8px' }} />
                        </div>
                        <div className="organization-settings__head" style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <div className="organization-settings-subtitle">Enabled Modules</div>
                          <InfoTooltip text="Each toggle below shows or hides that section inside the member portal for your members." placement="bottom" useFixed style={{ marginLeft: '6px', marginTop: '14px' }} />
                        </div>
                        <div className="organization-settings__content pl-5">
                        <div className="organization-settings__content-field">
                            <MyCheckbox
                                name={`enableMembers`}
                                title="Members"
                                handlechange={(event) => {
                                  formik.setFieldValue("enableMembers", event.target.checked);
                                }}
                                checked={formik.values?.enableMembers}
                              />
                          </div>
                          <div className="organization-settings__content-field">
                            <MyCheckbox
                                name={`enableYahrzeit`}
                                title="Yahrzeit"
                                handlechange={(event) => {
                                  formik.setFieldValue("enableYahrzeit", event.target.checked);
                                }}
                                checked={formik.values?.enableYahrzeit}
                              />
                          </div>
                          <div className="organization-settings__content-field">
                            <MyCheckbox
                                name={`enableEducation`}
                                title="Education"
                                handlechange={(event) => {
                                  formik.setFieldValue("enableEducation", event.target.checked);
                                }}
                                checked={formik.values?.enableEducation}
                              />
                          </div>
                          <div className="organization-settings__content-field">
                            <MyCheckbox
                                name={`enableBilling`}
                                title="Billing"
                                handlechange={(event) => {
                                  formik.setFieldValue("enableBilling", event.target.checked);
                                }}
                                checked={formik.values?.enableBilling}
                              />
                          </div>
                          <div className="organization-settings__content-field">
                            <MyCheckbox
                                name={`enableEvents`}
                                title="Events"
                                handlechange={(event) => {
                                  formik.setFieldValue("enableEvents", event.target.checked);
                                }}
                                checked={formik.values?.enableEvents}
                              />
                          </div>
                          <div className="organization-settings__content-field">
                            <MyCheckbox
                                name={`enableDonations`}
                                title="Donations"
                                handlechange={(event) => {
                                  formik.setFieldValue("enableDonations", event.target.checked);
                                }}
                                checked={formik.values?.enableDonations}
                              />
                          </div>
                          <div className="organization-settings__content-field organization-settings__directory">
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <MyCheckbox
                                  name={`memberPortalDirectory`}
                                  title="Directory"
                                  handlechange={(event) => {
                                    formik.setFieldValue("memberPortalDirectory", event.target.checked);
                                  }}
                                  checked={formik.values?.memberPortalDirectory}
                                />
                                <InfoTooltip text="Lets members browse a directory of other members in the portal. The Download button exports this list regardless of whether this is enabled." placement="bottom" useFixed style={{ marginLeft: '6px', marginBottom: '8px' }} />
                              </div>
                              <Button
                                className="mt-5"
                                onClick={downloadDirectory}
                                disabled={downloadDirectoryLoading}
                                variant="primary"
                                size="sm"
                              >
                                {downloadDirectoryLoading ? (
                                  <span className="mr-5 spinner spinner-white"></span>
                                ) : (
                                  <>
                                  <ExportYahIcon className="mr-2" />
                                  Download
                                  </>
                                )}
                              </Button>
                          </div>
                          <div className="organization-settings__content-field">
                            <MyCheckbox
                                name={`enableSettings`}
                                title="Settings"
                                handlechange={(event) => {
                                  formik.setFieldValue("enableSettings", event.target.checked);
                                }}
                                checked={formik.values?.enableSettings}
                              />
                          </div>
                        </div>
                        <div className="organization-settings__head mt-4" style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <div className="organization-settings-subtitle">Members portal announcement</div>
                          <InfoTooltip text="Update the message board in the members portal using the text box below." useFixed style={{ marginLeft: '6px', marginTop: '14px' }} />
                        </div>
                        <div className="mt-2">
                          <TextEditor name="announcement" />
                        </div>
                      </animated.div>
                  </div>
              </Card>
              {formik.values?.memberPortalApp && (
                <Card className="card card-custom p-4 mt-4">
                  <div className="organization-settings">
                    <div className="organization-settings__head">
                      <div className="organization-settings-label">Member App Settings</div>
                    </div>
                    <div className="organization-settings__head mt-4" style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <div className="organization-settings-subtitle">Member app announcement</div>
                      <InfoTooltip text="Update the message board in the members app using the text box below." style={{ marginLeft: '6px', marginTop: '14px' }} />
                    </div>
                    <div className="mt-2">
                      <TextEditor name="memberAppAnnouncement" />
                    </div>
                  </div>
                </Card>
              )}
            </div>
            <div className="profile-information col-md-7">
              <Card className="card card-custom p-4 mt-4 ">
                {
                  <PersonalInfoEdit
                    userInfo={userInfo}
                    setRefresh={setRefresh}
                    data={() => setUpdateData(true)}
                    formik={formik}
                    setPictures={setPictures}
                  />
                }
              </Card>
            </div>
          </div>
        </div>
      </div>
      )}
    </FormikProvider>
  );
};
export default SettingsPage;
